import { useEffect, useMemo, useState } from "react";
import {
  adminCreateRelationship,
  adminDeleteRelationship,
  adminListPhotographs,
  adminListRelationships,
  adminUpdateRelationship,
} from "../../lib/api";

export default function AdminRelationships() {
  const [rels, setRels] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filterSrc, setFilterSrc] = useState("");
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // create form
  const [src, setSrc] = useState("");
  const [dst, setDst] = useState("");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);

  async function refresh() {
    setLoading(true);
    setError("");
    try {
      const [r, p] = await Promise.all([adminListRelationships(), adminListPhotographs()]);
      setRels(r);
      setPhotos(p);
    } catch { setError("Failed to load"); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  const photoLabel = useMemo(() => {
    const m = {};
    photos.forEach((p) => { m[p.id] = `${p.id} · ${p.title || "Untitled"}`; });
    return m;
  }, [photos]);

  const filtered = useMemo(() => {
    if (!filterSrc) return rels;
    return rels.filter((r) => r.source_id === filterSrc || r.destination_id === filterSrc);
  }, [rels, filterSrc]);

  async function onCreate(e) {
    e.preventDefault();
    if (!src || !dst || !q.trim()) return;
    setCreating(true); setError("");
    try {
      await adminCreateRelationship({ source_id: src, destination_id: dst, question: q.trim() });
      setSrc(""); setDst(""); setQ("");
      await refresh();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Create failed");
    } finally { setCreating(false); }
  }

  async function onDelete(r) {
    if (!window.confirm(`Delete this question from ${r.source_id} → ${r.destination_id}?\n\n"${r.question}"`)) return;
    setBusyId(r.id);
    try { await adminDeleteRelationship(r.id); await refresh(); }
    catch { setError("Delete failed"); }
    finally { setBusyId(null); }
  }

  return (
    <div data-testid="admin-relationships-page" className="space-y-10">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="font-serif-display italic text-2xl md:text-3xl text-white/85">Questions</h1>
          <p className="mt-1 text-sm text-white/50 font-serif-display italic">{rels.length} routes total</p>
        </div>
        <label className="text-sm font-serif-display italic text-white/60 flex items-center gap-3">
          Filter by photograph
          <select
            value={filterSrc}
            onChange={(e) => setFilterSrc(e.target.value)}
            className="q-input !text-sm !py-2"
            data-testid="admin-rels-filter"
            style={{ minWidth: 200 }}
          >
            <option value="" style={{ background: "#0b0c0e" }}>— all —</option>
            {photos.map((p) => (
              <option key={p.id} value={p.id} style={{ background: "#0b0c0e" }}>{photoLabel[p.id]}</option>
            ))}
          </select>
        </label>
      </header>

      {/* Create form */}
      <section className="border border-white/10 rounded-md p-6 bg-black/20">
        <h2 className="font-serif-display italic text-lg text-white/85 mb-4">Add a new question</h2>
        <form onSubmit={onCreate} data-testid="admin-rel-create-form" className="grid gap-4 md:grid-cols-3">
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.22em] text-white/45 mb-1 font-serif-display italic">Source</span>
            <select value={src} onChange={(e) => setSrc(e.target.value)} className="q-input" required data-testid="admin-rel-src">
              <option value="" style={{ background: "#0b0c0e" }}>—</option>
              {photos.map((p) => (
                <option key={p.id} value={p.id} style={{ background: "#0b0c0e" }}>{photoLabel[p.id]}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.22em] text-white/45 mb-1 font-serif-display italic">Destination</span>
            <select value={dst} onChange={(e) => setDst(e.target.value)} className="q-input" required data-testid="admin-rel-dst">
              <option value="" style={{ background: "#0b0c0e" }}>—</option>
              {photos.map((p) => (
                <option key={p.id} value={p.id} style={{ background: "#0b0c0e" }}>{photoLabel[p.id]}</option>
              ))}
            </select>
          </label>
          <label className="block md:col-span-3">
            <span className="block text-xs uppercase tracking-[0.22em] text-white/45 mb-1 font-serif-display italic">Question</span>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} required minLength={2} maxLength={280} className="q-input" data-testid="admin-rel-question" />
          </label>
          <div className="md:col-span-3 flex items-center gap-4">
            <button type="submit" disabled={creating || !src || !dst || !q.trim()} className="btn-ghost disabled:opacity-40" data-testid="admin-rel-submit">
              {creating ? "Adding…" : "Add question"}
            </button>
            {error && <span className="text-sm text-red-300/80 font-serif-display italic">{error}</span>}
          </div>
        </form>
      </section>

      <section>
        {loading && <p className="text-sm text-white/45 font-serif-display italic">Loading…</p>}
        <ul className="space-y-3">
          {filtered.map((r) => (
            <RelRow
              key={r.id}
              rel={r}
              photos={photos}
              photoLabel={photoLabel}
              busy={busyId === r.id}
              isEditing={editing === r.id}
              onEditToggle={() => setEditing(editing === r.id ? null : r.id)}
              onSaved={() => { setEditing(null); refresh(); }}
              onDelete={() => onDelete(r)}
            />
          ))}
        </ul>
        {!filtered.length && !loading && <p className="text-sm text-white/45 font-serif-display italic">No questions.</p>}
      </section>
    </div>
  );
}

function RelRow({ rel, photos, photoLabel, busy, isEditing, onEditToggle, onSaved, onDelete }) {
  const [q, setQ] = useState(rel.question);
  const [src, setSrc] = useState(rel.source_id);
  const [dst, setDst] = useState(rel.destination_id);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { setQ(rel.question); setSrc(rel.source_id); setDst(rel.destination_id); }, [rel, isEditing]);

  async function save() {
    setSaving(true); setErr("");
    try {
      await adminUpdateRelationship(rel.id, { source_id: src, destination_id: dst, question: q.trim() });
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <li data-testid={`admin-rel-row-${rel.id}`} className="border border-white/10 rounded-md p-4 bg-black/20">
      <div className="flex items-baseline gap-3">
        <span className="font-mono-editor text-xs text-white/45 whitespace-nowrap">{rel.source_id} → {rel.destination_id}</span>
        <span className="font-serif-display italic text-white/80 truncate">{rel.question}</span>
      </div>
      {isEditing && (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1 font-serif-display italic">Source</span>
              <select value={src} onChange={(e) => setSrc(e.target.value)} className="q-input text-base">
                {photos.map((p) => (<option key={p.id} value={p.id} style={{ background: "#0b0c0e" }}>{photoLabel[p.id]}</option>))}
              </select>
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1 font-serif-display italic">Destination</span>
              <select value={dst} onChange={(e) => setDst(e.target.value)} className="q-input text-base">
                {photos.map((p) => (<option key={p.id} value={p.id} style={{ background: "#0b0c0e" }}>{photoLabel[p.id]}</option>))}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1 font-serif-display italic">Question</span>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} className="q-input text-base" />
          </label>
          {err && <p className="text-xs text-red-300/80 font-serif-display italic">{err}</p>}
          <div className="flex items-center gap-3">
            <button type="button" onClick={save} disabled={saving} className="btn-ghost disabled:opacity-40">{saving ? "Saving…" : "Save"}</button>
            <button type="button" onClick={onEditToggle} className="text-xs text-white/50 hover:text-white font-serif-display italic">Cancel</button>
          </div>
        </div>
      )}
      <div className="mt-3 flex items-center gap-4 text-[11px]">
        <button type="button" onClick={onEditToggle} className="text-white/60 hover:text-white nav-link" data-testid={`admin-rel-edit-${rel.id}`}>
          {isEditing ? "Close" : "Edit"}
        </button>
        <button type="button" disabled={busy} onClick={onDelete} className="text-red-300/70 hover:text-red-200 nav-link" data-testid={`admin-rel-delete-${rel.id}`}>
          Delete
        </button>
      </div>
    </li>
  );
}
